<script setup>
/**
 * Konuşma içi İade/Değişim Popup'ı
 * ---------------------------------
 * Cevap kutusundaki ikon sırasından açılır. Konuşmadaki müşterinin adı ve
 * telefonu HAZIR gelir, siparişleri otomatik aranır; temsilci sipariş ve
 * ürün(ler)i seçer, sebep/tercihi girer, "Kodu Ver" der.
 * Bilgilendirme mesajı temsilcinin seçimine göre gider:
 *   - Bu konuşmadan (varsayılan — IG/WhatsApp/web hangisiyse oraya)
 *   - WhatsApp (Koza 0545 hattı)
 *   - Gönderme (temsilci elle yazar)
 */
import { ref, computed, onMounted } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { postMarketplaceJsonBody } from 'dashboard/helpers/marketplaceApi';
import ReturnOrderPicker from 'dashboard/components-next/Returns/ReturnOrderPicker.vue';

const emit = defineEmits(['close']);

const currentChat = useMapGetter('getSelectedChat');
const accountId = useMapGetter('getCurrentAccountId');

const contact = computed(() => currentChat.value?.meta?.sender || {});
const inboxName = computed(
  () => currentChat.value?.meta?.channel?.replace('Channel::', '') || ''
);

const selection = ref({ order: null, items: [], total: 0 });
const reason = ref('');
// hazır sebepler (geçmiş 1000+ kaydın gruplaması; ReturnCenter ile aynı liste)
const REASON_OPTIONS = [
  'Beden büyük geldi',
  'Beden küçük geldi',
  'Kalıp/beden sorunu',
  'Kalitesini/modelini beğenmedi',
  'Rengini beğenmedi',
  'Farklı ürün/renk ile değişim istiyor',
  'Yanlış ürün/renk/beden gönderildi (bizim hata)',
  'Kusurlu ürün gönderildi (bizim hata)',
  'Eksik ürün gönderildi (bizim hata)',
  'Vazgeçti',
  'Diğer',
];
const reasonChoice = ref('');
const reasonCustom = ref('');
const fault = ref('customer');
const resolution = ref('');
const notes = ref('');
const amount = ref('');
const notifyVia = ref('conversation');
const creating = ref(false);
const error = ref('');
const created = ref(null);

const customerName = ref('');
const customerPhone = ref('');
const channel = ref('ticimax');

const initialQuery = computed(
  () => contact.value?.phone_number || contact.value?.name || ''
);

const NOTIFY_OPTIONS = [
  { key: 'conversation', label: 'Bu konuşmadan gönder' },
  { key: 'whatsapp', label: 'WhatsApp (0545 ortak hat)' },
  { key: 'none', label: 'Mesaj gönderme' },
];

const onSelection = sel => {
  selection.value = sel;
  const o = sel.order;
  if (o) {
    customerName.value = o.customerName || customerName.value;
    customerPhone.value = o.phone || customerPhone.value;
    channel.value = o.channel;
  }
  if (sel.items.length) amount.value = sel.total.toFixed(2);
};

const submit = async () => {
  if (!customerName.value && !customerPhone.value) {
    error.value = 'Müşteri adı veya telefon gerekli';
    return;
  }
  creating.value = true;
  error.value = '';
  try {
    created.value = await postMarketplaceJsonBody(accountId.value, 'returns', {
      marketplace_order_id: selection.value.order?.id || null,
      order_number: selection.value.order?.orderNumber || '',
      channel: channel.value,
      customer_name: customerName.value,
      phone: customerPhone.value,
      amount: amount.value,
      reason:
        (reasonChoice.value === 'Diğer'
          ? reasonCustom.value
          : reasonChoice.value) || reason.value,
      fault: fault.value,
      resolution: resolution.value,
      notes: notes.value,
      items: selection.value.items,
      notify_via: notifyVia.value,
      conversation_display_id: currentChat.value?.id,
    });
  } catch (e) {
    error.value = e.message;
  } finally {
    creating.value = false;
  }
};

onMounted(() => {
  customerName.value = contact.value?.name || '';
  customerPhone.value = contact.value?.phone_number || '';
});
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    @click.self="emit('close')"
  >
    <div
      class="w-full max-w-3xl max-h-[85vh] overflow-auto rounded-2xl bg-n-solid-2 border border-n-weak shadow-xl flex flex-col gap-4 p-5"
    >
      <!-- başlık -->
      <div class="flex items-start justify-between gap-3">
        <div>
          <h2 class="text-xl font-bold text-n-slate-12">
            İade / Değişim Talebi
          </h2>
          <p class="text-sm text-n-slate-11 mt-0.5">
            {{ contact.name }}
            <template v-if="contact.phone_number">
              · {{ contact.phone_number }}
            </template>
            <template v-if="inboxName"> · Kanal: {{ inboxName }} </template>
          </p>
        </div>
        <button
          class="px-3 py-1.5 rounded-lg bg-n-solid-3 text-n-slate-12 font-semibold hover:bg-n-solid-1"
          @click="emit('close')"
        >
          Kapat
        </button>
      </div>

      <!-- başarı ekranı -->
      <div
        v-if="created"
        class="rounded-xl bg-green-100 text-green-900 p-5 flex flex-col gap-2"
      >
        <div class="text-2xl font-bold">✅ {{ created.code }}</div>
        <div class="text-base">
          Talep açıldı — {{ created.statusLabel }}.
          <template v-if="notifyVia === 'conversation'">
            Bilgilendirme mesajı bu konuşmaya yazıldı.
          </template>
          <template v-else-if="notifyVia === 'whatsapp'">
            Bilgilendirme WhatsApp'tan gönderildi.
          </template>
          <template v-else>
            Mesaj gönderilmedi — müşteriye siz bildirin.
          </template>
        </div>
        <div class="text-sm">
          Takip: Kişiler → İade / Değişim panelinden yürütülür.
        </div>
      </div>

      <template v-else>
        <div
          v-if="error"
          class="rounded-lg bg-ruby-100 text-ruby-900 px-4 py-2.5 text-base font-medium"
        >
          {{ error }}
        </div>

        <!-- sipariş + ürün seçimi (ortak bileşen; telefonla otomatik arar) -->
        <ReturnOrderPicker
          :account-id="accountId"
          :initial-query="initialQuery"
          @selection="onSelection"
        />

        <!-- talep alanları -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label class="flex flex-col gap-1 text-sm text-n-slate-11">
            Müşteri Adı
            <input
              v-model="customerName"
              class="rounded-lg border border-n-strong bg-n-solid-1 px-3 py-2 text-base text-n-slate-12"
            />
          </label>
          <label class="flex flex-col gap-1 text-sm text-n-slate-11">
            Telefon
            <input
              v-model="customerPhone"
              class="rounded-lg border border-n-strong bg-n-solid-1 px-3 py-2 text-base text-n-slate-12"
            />
          </label>
          <label class="flex flex-col gap-1 text-sm text-n-slate-11">
            Sebep
            <select
              v-model="reasonChoice"
              class="rounded-lg border border-n-strong bg-n-solid-1 px-3 py-2 text-base text-n-slate-12"
              @change="
                reasonChoice.includes('bizim hata') && (fault = 'company')
              "
            >
              <option value="">— Sebep seçin —</option>
              <option v-for="opt in REASON_OPTIONS" :key="opt" :value="opt">
                {{ opt }}
              </option>
            </select>
          </label>
          <label
            v-if="reasonChoice === 'Diğer'"
            class="flex flex-col gap-1 text-sm text-n-slate-11"
          >
            Sebep (elle yazın)
            <input
              v-model="reasonCustom"
              placeholder="kısaca yazın"
              class="rounded-lg border border-n-strong bg-n-solid-1 px-3 py-2 text-base text-n-slate-12"
            />
          </label>
          <label class="flex flex-col gap-1 text-sm text-n-slate-11">
            Hata Kimde? (kargo kodunu belirler)
            <select
              v-model="fault"
              class="rounded-lg border border-n-strong bg-n-solid-1 px-3 py-2 text-base text-n-slate-12"
            >
              <option value="customer">
                Müşteri kaynaklı (beğenmedi/beden) — kargo müşteriye ait
              </option>
              <option value="company">
                Bizim hatamız (yanlış/kusurlu ürün) — kargo bize ait
              </option>
            </select>
          </label>
          <label class="flex flex-col gap-1 text-sm text-n-slate-11">
            Müşteri Tercihi (biliniyorsa)
            <select
              v-model="resolution"
              class="rounded-lg border border-n-strong bg-n-solid-1 px-3 py-2 text-base text-n-slate-12"
            >
              <option value="">Henüz bilinmiyor</option>
              <option value="refund">İade (para iadesi)</option>
              <option value="exchange">Değişim</option>
            </select>
          </label>
          <label class="flex flex-col gap-1 text-sm text-n-slate-11">
            İade Tutarı (TL)
            <input
              v-model="amount"
              type="number"
              step="0.01"
              class="rounded-lg border border-n-strong bg-n-solid-1 px-3 py-2 text-base text-n-slate-12"
            />
          </label>
          <label class="flex flex-col gap-1 text-sm text-n-slate-11">
            Not
            <input
              v-model="notes"
              class="rounded-lg border border-n-strong bg-n-solid-1 px-3 py-2 text-base text-n-slate-12"
            />
          </label>
        </div>

        <!-- bildirim kanalı: temsilcinin insiyatifinde -->
        <div class="flex flex-col gap-1.5">
          <span class="text-sm font-semibold text-n-slate-11">
            Bilgilendirme mesajı nereden gitsin?
          </span>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="opt in NOTIFY_OPTIONS"
              :key="opt.key"
              class="px-3 py-2 rounded-lg border text-sm font-semibold"
              :class="
                notifyVia === opt.key
                  ? 'bg-woot-600 border-woot-600 text-white'
                  : 'bg-n-solid-1 border-n-weak text-n-slate-11 hover:border-woot-400'
              "
              @click="notifyVia = opt.key"
            >
              {{ opt.label }}
            </button>
          </div>
        </div>

        <button
          class="self-start px-6 py-3 rounded-xl bg-green-600 text-white text-lg font-bold hover:bg-green-700 disabled:opacity-50"
          :disabled="creating"
          @click="submit"
        >
          {{ creating ? 'Kaydediliyor…' : 'Kodu Ver' }}
        </button>
      </template>
    </div>
  </div>
</template>
